/**
 * Maintenance-schedule trigger metadata.
 *
 * The API accepts six trigger types, but the picker is deliberately narrower
 * than the contract:
 *
 *  - `meters_run` is never offered. The evaluator cannot evaluate it (footage
 *    attribution across multi-ticket jobs is undecided), so a schedule created
 *    with it sits permanently at status `unknown`.
 *  - Only one odometer unit is offered — the company's own unit system. Showing
 *    both km and mi invites schedules whose interval is in a unit the unit's
 *    meter readings are not recorded in, which evaluates to `unknown` forever.
 *  - `job_count` is equipment-only and the meter triggers are vehicle-only,
 *    matching how meters are recorded per asset kind.
 */
import type {
  AssetKind,
  BulkScheduleResult,
  BulkSkippedUnit,
  MaintenanceSchedule,
  MaintenanceScheduleStatus,
  MaintenanceTriggerType,
} from '../types/equipment';
import { formatCalendarDate } from './calendar-date';
import { fmtQty } from './display-labels';

export interface TriggerOption {
  value: MaintenanceTriggerType;
  /** Picker label. */
  label: string;
  /** Unit the interval is counted in, e.g. "days", "km", "hours". */
  unit: string;
  /** Placeholder hint for the interval input. */
  placeholder: string;
}

const TRIGGER_META: Record<MaintenanceTriggerType, { label: string; unit: string; placeholder: string }> = {
  calendar_days: { label: 'Calendar days',  unit: 'days',  placeholder: 'e.g. 90' },
  odometer_km:   { label: 'Odometer (km)',  unit: 'km',    placeholder: 'e.g. 15000' },
  odometer_mi:   { label: 'Odometer (mi)',  unit: 'mi',    placeholder: 'e.g. 10000' },
  engine_hours:  { label: 'Engine hours',   unit: 'hours', placeholder: 'e.g. 250' },
  job_count:     { label: 'Job count',      unit: 'jobs',  placeholder: 'e.g. 25' },
  meters_run:    { label: 'Metres run',     unit: 'm',     placeholder: 'e.g. 1000' },
};

/** Triggers whose "last performed" carries a meter value alongside the date. */
const METER_TRIGGERS: MaintenanceTriggerType[] = [
  'odometer_km',
  'odometer_mi',
  'engine_hours',
  'meters_run',
];

export function isMeterTrigger(trigger: MaintenanceTriggerType): boolean {
  return METER_TRIGGERS.includes(trigger);
}

/** Display label for any trigger, including ones the picker never offers. */
export function triggerLabel(trigger: MaintenanceTriggerType | string): string {
  return TRIGGER_META[trigger as MaintenanceTriggerType]?.label ?? String(trigger);
}

export function triggerUnit(trigger: MaintenanceTriggerType | string): string {
  return TRIGGER_META[trigger as MaintenanceTriggerType]?.unit ?? '';
}

export function triggerPlaceholder(trigger: MaintenanceTriggerType | string): string {
  return TRIGGER_META[trigger as MaintenanceTriggerType]?.placeholder ?? '';
}

/**
 * The triggers a tenant may pick for a new schedule on this unit.
 *
 * Anything that is not explicitly a vehicle is treated as equipment — an
 * unset `asset_kind` gets the conservative (no-meter) option set.
 */
export function triggerOptionsFor(
  assetKind: AssetKind | string | null | undefined,
  isMetric: boolean,
): TriggerOption[] {
  const odometer: MaintenanceTriggerType = isMetric ? 'odometer_km' : 'odometer_mi';
  const triggers: MaintenanceTriggerType[] =
    assetKind === 'vehicle'
      ? ['calendar_days', odometer, 'engine_hours']
      : ['calendar_days', 'job_count'];

  return triggers.map((value) => ({ value, ...TRIGGER_META[value] }));
}

/* ── Status chips ──────────────────────────────────────────────────── */

/**
 * Chip classes mirror the rentals work-order schedule chips
 * (`scheduleStatusBadge` in pages/rentals/MaintenanceWorkOrders.tsx) so due
 * state reads identically across the app. `unknown` is the extra state this
 * API can return — a schedule it declined to evaluate.
 *
 * `needs_baseline` is blue, matching the PDF's print colour (#2b6cb0): it says
 * "no service was ever recorded on this rule", which is a data question, not a
 * claim that work is late. Colouring it amber or red would assert exactly the
 * lateness the status exists to stop asserting.
 *
 * THE INK IS A THEME-AWARE TOKEN, NOT A HUE UTILITY. The wash and the edge stay
 * in the hue; the words ride on `--sched-*`, which theme.css defines twice.
 * No single shade clears 4.5:1 on the wash in both themes, so the light half
 * is a darker twin rather than a compromise value. The numbers are in
 * theme.css beside the tokens and are recomputed by
 * maintenanceTriggers.contrast.test.ts.
 */
export const SCHEDULE_STATUS_BADGE: Record<MaintenanceScheduleStatus, { label: string; cls: string }> = {
  ok:             { label: 'OK',              cls: 'bg-emerald-500/15 text-sched-ok border-emerald-500/20' },
  due_soon:       { label: 'Due Soon',        cls: 'bg-amber-500/15 text-sched-due-soon border-amber-500/20' },
  overdue:        { label: 'Overdue',         cls: 'bg-red-500/15 text-sched-overdue border-red-500/20' },
  needs_baseline: { label: 'Never Serviced',  cls: 'bg-blue-500/15 text-sched-baseline border-blue-500/20' },
  unknown:        { label: 'Unknown',         cls: 'bg-fieldiq-dark text-fieldiq-muted border-fieldiq-border' },
};

export function scheduleStatus(schedule: MaintenanceSchedule): MaintenanceScheduleStatus {
  const raw = schedule.evaluation?.status;
  return raw && raw in SCHEDULE_STATUS_BADGE ? raw : 'unknown';
}

/* ── Formatting ────────────────────────────────────────────────────── */

/** @see fmtQty — thousands separators, no trailing zeros, absent is '—' not '0'. */
const fmtNum = fmtQty;

export function formatInterval(schedule: MaintenanceSchedule): string {
  if (schedule.intervalValue == null) return '—';
  return `Every ${fmtNum(schedule.intervalValue)} ${triggerUnit(schedule.triggerType)}`.trim();
}

/**
 * Date-only display. Tolerates full ISO timestamps and `YYYY-MM-DD HH:MM:SS+00`
 * — parseCalendarDate splits on both T and space, so that tolerance survives.
 * An unparseable value still falls back to its own text rather than an em dash,
 * which is the one thing this wanted that the helper does not decide.
 */
export function formatScheduleDate(value: string | null | undefined): string {
  if (!value) return '—';
  return formatCalendarDate(value, { month: 'short', day: 'numeric', year: 'numeric' }) ?? String(value);
}

/**
 * "Last performed" — a date for calendar/job triggers, a date plus the meter
 * value it was performed at for meter triggers.
 */
export function formatLastPerformed(schedule: MaintenanceSchedule): string {
  if (!schedule.lastPerformedAt && schedule.lastPerformedValue == null) return 'Never';
  const date = schedule.lastPerformedAt ? formatScheduleDate(schedule.lastPerformedAt) : '—';
  if (schedule.lastPerformedValue == null) return date;
  return `${date} @ ${fmtNum(schedule.lastPerformedValue)} ${triggerUnit(schedule.triggerType)}`.trim();
}

/**
 * "Next due" — the server's computed date or meter target. Falls back to the
 * stored column when the evaluator returned nothing.
 */
export function formatNextDue(schedule: MaintenanceSchedule): string {
  const evaluation = schedule.evaluation;
  const dueAt = evaluation?.next_due_at ?? schedule.nextDueAt;
  const dueValue = evaluation?.next_due_value ?? schedule.nextDueValue;

  if (dueValue != null) {
    return `${fmtNum(dueValue)} ${triggerUnit(schedule.triggerType)}`.trim();
  }
  if (dueAt) return formatScheduleDate(dueAt);
  return '—';
}

/**
 * How much runway is left, when the server computed it — "4 days left",
 * "1,200 km overdue".
 */
export function formatRemaining(schedule: MaintenanceSchedule): string | null {
  const remaining = schedule.evaluation?.remaining;
  if (remaining == null || !Number.isFinite(remaining)) return null;
  const unit = schedule.evaluation?.unit || triggerUnit(schedule.triggerType);
  return remaining < 0
    ? `${fmtNum(Math.abs(remaining))} ${unit} overdue`.trim()
    : `${fmtNum(remaining)} ${unit} left`.trim();
}

/* ── Bulk apply result ─────────────────────────────────────────────── */

/**
 * Normalise the bulk endpoint's response into created/skipped counts plus
 * named skipped units.
 *
 * The bulk route's success shape is not pinned down in the API contract (and
 * as of 2026-07-26 the deployed API 404s on it), so read every plausible
 * spelling rather than rendering a blank result. `resolveName` fills in unit
 * names when the response only carries ids — a skipped unit nobody can name
 * reads as a silent failure.
 */
export function normalizeBulkResult(
  raw: any,
  selectedIds: string[],
  resolveName: (id: string) => string,
): BulkScheduleResult {
  const body = raw?.data ?? raw ?? {};
  const counts = body.counts ?? {};

  const countOf = (...candidates: any[]): number | null => {
    for (const candidate of candidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
      if (Array.isArray(candidate)) return candidate.length;
    }
    return null;
  };

  const createdRaw = body.created ?? body.createdSchedules ?? body.created_schedules;
  const skippedRaw =
    body.skipped ?? body.skippedUnits ?? body.skipped_units ?? body.skipped_equipment;

  const createdCount =
    countOf(createdRaw, counts.created, body.created_count, body.createdCount) ?? 0;
  const skippedCount =
    countOf(skippedRaw, counts.skipped, body.skipped_count, body.skippedCount) ?? 0;

  const skippedEntries: any[] = Array.isArray(skippedRaw) ? skippedRaw : [];
  const skipped: BulkSkippedUnit[] = skippedEntries.map((entry) => {
    if (typeof entry === 'string') {
      return { equipmentId: entry, name: resolveName(entry), reason: null };
    }
    const id = entry?.equipment_id ?? entry?.equipmentId ?? entry?.id ?? null;
    const name =
      entry?.name ??
      entry?.equipment_name ??
      entry?.equipmentName ??
      entry?.unit_number ??
      entry?.unitNumber ??
      (id ? resolveName(id) : 'Unknown unit');
    return {
      equipmentId: id,
      name,
      reason: entry?.reason ?? entry?.error ?? entry?.message ?? null,
    };
  });

  // A skip count with nobody named: derive the names from the ids that did not
  // come back as created.
  if (skipped.length === 0 && skippedCount > 0) {
    const createdIds = new Set<string>(
      (Array.isArray(createdRaw) ? createdRaw : [])
        .map((c: any) => (typeof c === 'string' ? c : c?.equipment_id ?? c?.equipmentId ?? c?.id))
        .filter(Boolean),
    );
    if (createdIds.size > 0) {
      selectedIds
        .filter((id) => !createdIds.has(id))
        .forEach((id) => skipped.push({ equipmentId: id, name: resolveName(id), reason: null }));
    }
  }

  return { createdCount, skippedCount, skipped };
}
